import { action, atom, noop } from '@reatom/framework'
import i18next from 'i18next'

import { Result } from '@blade/std-lib'
import { userAtom } from '@root/entities/auth'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'
import { showErrorSnackbarAction } from '@root/shared/ui/error-snackbar'
import { showSnackbarAction } from '@root/shared/ui-kit'

import type { EditPasswordStage } from './update-password'
import type { Redacted } from '@blade/std-lib'

const checkLocalPasswordAction = action(
  async (ctx, password: Redacted.Redacted) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)
    const currentUser = ctx.get(userAtom)
    if (!currentUser) {
      return Result.fail(Error.UnknownError.fromUnknown())
    }
    const result = await ctx.schedule(() =>
      secureStorageService.verifyPassword(currentUser.delugeId, password),
    )
    if (Result.isFailure(result)) {
      return Result.fail(Error.UnknownError.fromUnknown(result.failure))
    }

    return Result.succeed({})
  },
  'checkLocalPasswordAction',
)

const updateLocalPasswordAction = action(
  async (
    ctx,
    oldPassword: Redacted.Redacted,
    newPassword: Redacted.Redacted,
  ) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)
    const currentUser = ctx.get(userAtom)

    if (!currentUser) {
      return Result.fail(Error.UnknownError.fromUnknown())
    }

    const result = await ctx.schedule(() =>
      secureStorageService.changePassword(
        currentUser.delugeId,
        oldPassword,
        newPassword,
      ),
    )
    if (Result.isFailure(result)) {
      return Result.fail(Error.UnknownError.fromUnknown(result.failure))
    }

    return Result.succeed({})
  },
  'updateLocalPasswordAction',
)

const reatomCheckCurrentLocalPassword = () => {
  const theAtom = reatomRequest({
    execAction: checkLocalPasswordAction,
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'checkLocalPassword',
  })

  return theAtom
}
const reatomUpdateLocalPassword = () => {
  const theAtom = reatomRequest({
    execAction: updateLocalPasswordAction,
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'updatePassword',
  })

  return theAtom
}

export const reatomLocalPasswordEditFlow = () => {
  const passwordUpdatedEvent = action(noop, 'passwordUpdatedEvent')
  const stageAtom = atom<EditPasswordStage>(
    { type: 'current-password' },
    'stageAtom',
  )

  // eslint-disable-next-line @reatom/unit-naming-rule
  const updateLocalPasswordModel = reatomUpdateLocalPassword()

  // eslint-disable-next-line @reatom/unit-naming-rule
  const checkLocalPasswordModel = reatomCheckCurrentLocalPassword()

  const checkCurrentPasswordAction = action(
    async (ctx, currentPassword: Redacted.Redacted) => {
      const result = await ctx.schedule(() =>
        checkLocalPasswordModel.execute(ctx, currentPassword),
      )

      if (Result.isFailure(result)) {
        showErrorSnackbarAction(ctx, result.failure)
        return
      }

      stageAtom(ctx, { type: 'new-password', currentPassword })
    },
    'checkCurrentPasswordAction',
  )

  const setNewPasswordAction = action(
    async (ctx, newPassword: Redacted.Redacted) => {
      const stage = ctx.get(stageAtom)
      if (stage.type !== 'new-password') {
        return
      }
      stageAtom(ctx, {
        type: 'repeat',
        newPassword,
        currentPassword: stage.currentPassword,
      })
    },
    'setNewPasswordAction',
  )

  const confirmNewPasswordAction = action(
    async (ctx, newPassword: Redacted.Redacted) => {
      const stage = ctx.get(stageAtom)

      if (stage.type !== 'repeat') {
        return
      }

      const result = await ctx.schedule(() =>
        updateLocalPasswordModel.execute(
          ctx,
          stage.currentPassword,
          newPassword,
        ),
      )

      if (Result.isSuccess(result)) {
        showSnackbarAction(ctx, {
          text: i18next.t('settings.local-password.toast-changed'),
          intent: 'success',
        })
        passwordUpdatedEvent(ctx)
        return
      }

      showSnackbarAction(ctx, {
        text: i18next.t('common.something-went-wrong'),
        intent: 'error',
      })
    },
    'confirmNewPasswordAction',
  )

  return {
    passwordUpdatedEvent,
    stageAtom,
    checkCurrentPasswordAction,
    setNewPasswordAction,
    confirmNewPasswordAction,
    updatePasswordStateAtom: updateLocalPasswordModel,
    checkPasswordStateAtom: checkLocalPasswordModel,
  }
}

export type LocalPasswordEditFlowReatom = ReturnType<
  typeof reatomLocalPasswordEditFlow
>
